import type { EventTicketKind, NewTicketKind, TicketKindUpdate } from "@hobbyshopify/backend-client";
import { parsePrice } from "./table-kind";

/** A kind of ticket as the form holds it: every field a string an input binds to. */
export interface TicketKindDraft {
  readonly name: string;
  readonly description: string;
  /** In the shop's currency, as typed: "5" or "5.50". */
  readonly price: string;
  /** How many there are; blank for no limit. */
  readonly capacity: string;
}

export type TicketKindField = keyof TicketKindDraft;
export type TicketKindErrors = Partial<Record<TicketKindField, string>>;

export const EMPTY_TICKET_KIND: TicketKindDraft = { name: "", description: "", price: "", capacity: "" };

export const MAX_TICKETS = 100_000;
export const MAX_TICKET_PRICE_CENTS = 10_000_000;

/** The tickets could not be read, for a reason other than a refusal. */
export const TICKETS_FAILED_TO_LOAD = "The tickets could not be read. Reload the page to try again.";

/** Adding, changing or removing a kind failed on its way: it may have been done all the same. */
export const TICKETS_FAILED_ON_THE_WAY =
  "Something went wrong on the way. Look at the tickets before trying again: the change may have been made.";

export function ticketKindDraft(kind: EventTicketKind): TicketKindDraft {
  return {
    name: kind.name,
    description: kind.description ?? "",
    price: (kind.priceCents / 100).toFixed(2),
    capacity: kind.capacity === null ? "" : String(kind.capacity),
  };
}

/** The form's own checks. A free ticket costs 0; a blank count is no limit. */
export function validateTicketKindDraft(draft: TicketKindDraft): TicketKindErrors {
  const errors: TicketKindErrors = {};
  if (!draft.name.trim()) errors.name = "Name the kind of ticket.";
  const price = parsePrice(draft.price);
  if (price === null) errors.price = "Enter a price, like 5 or 5.50. A free ticket is 0.";
  else if (price > MAX_TICKET_PRICE_CENTS) errors.price = `Enter a price of ${MAX_TICKET_PRICE_CENTS / 100} at most.`;
  const capacity = draft.capacity.trim();
  if (capacity && (!/^\d+$/.test(capacity) || Number(capacity) < 1 || Number(capacity) > MAX_TICKETS)) {
    errors.capacity = `Enter how many, from 1 to ${MAX_TICKETS}, or leave it blank for no limit.`;
  }
  return errors;
}

export function draftToNewTicketKind(draft: TicketKindDraft): NewTicketKind {
  const description = draft.description.trim();
  const capacity = draft.capacity.trim();
  return {
    name: draft.name.trim(),
    ...(description ? { description } : {}),
    priceCents: parsePrice(draft.price) ?? 0,
    ...(capacity ? { capacity: Number(capacity) } : {}),
  };
}

/**
 * An edit sends only the fields the host changed in the form (`changed`), as it holds them now: the server
 * may hold newer values of the rest. A blank description clears it, and a blank count lifts the limit.
 */
export function draftToTicketKindUpdate(
  draft: TicketKindDraft,
  changed: ReadonlySet<TicketKindField>,
): TicketKindUpdate {
  const capacity = draft.capacity.trim();
  return {
    ...(changed.has("name") ? { name: draft.name.trim() } : {}),
    ...(changed.has("description") ? { description: draft.description.trim() || null } : {}),
    ...(changed.has("price") ? { priceCents: parsePrice(draft.price) ?? 0 } : {}),
    ...(changed.has("capacity") ? { capacity: capacity ? Number(capacity) : null } : {}),
  };
}

/** How many of a kind there are, in words: "200 tickets", "1 ticket", "No limit". */
export function capacityLine(kind: Pick<EventTicketKind, "capacity">): string {
  if (kind.capacity === null) return "No limit";
  return `${kind.capacity} ${kind.capacity === 1 ? "ticket" : "tickets"}`;
}
